// covered-by: frontends/carrie/src/components/ask-puntt-sources/__tests__/SourceHoverCard.test.tsx
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { IconButton, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SourceAvatar, SourceCountCluster } from "./SourceCountCluster";
import { SourcePreview, SourceTrustedFooter } from "./SourcePreview";
import { type AskPunttSource } from "./types";

export const SOURCE_HOVER_PANEL_CLASS =
  "max-h-[min(70vh,420px)] w-[331px] overflow-y-auto rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-0 shadow-lg";

export type SourceHoverPanelProps = {
  sources: AskPunttSource[];
  testIdPrefix: string;
  /** Stable id for the panel testid (group of sources at one citation). */
  groupId: string;
  className?: string;
};

/**
 * Presentational source hover body — used inside Radix HoverCard and as an
 * inline style-guide demo (portaled HoverCard content is easy to miss there).
 */
export function SourceHoverPanel({
  sources,
  testIdPrefix,
  groupId,
  className,
}: SourceHoverPanelProps) {
  const { t } = useTranslation("ui");
  const [index, setIndex] = useState(0);

  if (sources.length === 0) return null;

  const safeIndex = Math.min(index, sources.length - 1);
  const current = sources[safeIndex]!;
  const multi = sources.length > 1;
  // why: SourceAvatar falls back to the title's first two letters, which on a
  // claim citation reads as a stray monogram ("FO" for "For the..."). Only a
  // source that names its own initials gets an avatar.
  const hasAvatar = current.initials != null && current.initials.trim() !== "";

  const goPrev = () => {
    setIndex((prev) => (prev === 0 ? sources.length - 1 : prev - 1));
  };
  const goNext = () => {
    setIndex((prev) => (prev === sources.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      data-testid={`${testIdPrefix}-source-hover-${groupId}`}
      aria-label={t("askPuntt.sources.hoverAria")}
      className={[SOURCE_HOVER_PANEL_CLASS, className]
        .filter(Boolean)
        .join(" ")}
    >
      {multi ? (
        <div className="flex items-center justify-between border-b border-puntt-neutral-gray-6 px-4 py-2">
          <div className="flex items-center gap-2">
            <IconButton
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              data-testid={`${testIdPrefix}-source-hover-prev`}
              aria-label={t("askPuntt.sources.previous")}
              onClick={goPrev}
            >
              <CaretLeft size={16} aria-hidden="true" />
            </IconButton>
            <Text
              as="span"
              size="1"
              weight="medium"
              data-testid={`${testIdPrefix}-source-hover-index`}
              className="uppercase tracking-[0.04px] text-puntt-neutral-gray-11"
            >
              {t("askPuntt.sources.pageIndex", {
                current: safeIndex + 1,
                total: sources.length,
              })}
            </Text>
            <IconButton
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              data-testid={`${testIdPrefix}-source-hover-next`}
              aria-label={t("askPuntt.sources.next")}
              onClick={goNext}
            >
              <CaretRight size={16} aria-hidden="true" />
            </IconButton>
          </div>
          <SourceCountCluster
            sources={sources}
            testIdPrefix={`${testIdPrefix}-hover-header`}
            displayOnly
          />
        </div>
      ) : null}
      <div className="flex flex-col p-4">
        <SourcePreview
          source={current}
          testIdPrefix={testIdPrefix}
          leading={
            hasAvatar ? (
              <SourceAvatar source={current} testIdPrefix={testIdPrefix} />
            ) : null
          }
        />
      </div>
      <SourceTrustedFooter source={current} testIdPrefix={testIdPrefix} />
    </div>
  );
}
